import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Coins, ShieldCheck, X, Loader2 } from "lucide-react";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { Button } from "@/components/ui/button";
import { PlanCard, CUSTOM_PLAN } from "@/components/nadia/PlanCard";
import { getBillingConfig, getWallet, goToCheckout, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const eur = (v) => `€${Number(v).toFixed(2).replace(".", ",")}`;

export default function Piani() {
  const [cfg, setCfg] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    getBillingConfig().then(setCfg).catch(() => {});
    if (user) getWallet().then(setWallet).catch(() => {});
  }, [user]);

  const activate = async (plan) => {
    if (!user) return nav(`/registrati?plan=${plan.id}`);
    setBusy(plan.id);
    try {
      await goToCheckout(nav, "subscription", plan.id);
    } catch (e) {
      toast.error(formatApiError(e, "Attivazione non riuscita"));
    } finally {
      setBusy(null);
    }
  };

  const buyFlex = async (pack) => {
    if (!user) return nav(`/registrati?plan=start`);
    setBusy(pack.id);
    try {
      await goToCheckout(nav, "credit_pack", pack.id);
    } catch (e) {
      toast.error(formatApiError(e, "Acquisto non riuscito"));
    } finally {
      setBusy(null);
    }
  };

  const current = wallet?.billing?.status === "active" ? wallet.billing.plan : null;
  const highlight = params.get("plan");

  return (
    <div className="min-h-screen px-4 py-8 lg:py-12">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start gap-4 mb-8">
          <NadiaAvatar size="lg" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-300/80">Nadia Cloud · Abbonamenti</div>
            <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]">Scegli come <span className="text-ai-gradient">far lavorare Nadia.</span></h1>
            <p className="mt-2 text-slate-400 text-sm sm:text-base max-w-2xl">Start e Pro includono gettoni per la chat e le elaborazioni AI. Ricariche da 10 € per 100 gettoni. Budget pubblicitario separato.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3" data-testid="plans-grid">
          {(cfg?.plans || []).filter((p) => p.id !== "flex").map((p) => (
            <div key={p.id} className={highlight === p.id ? "ring-1 ring-cyan-400/40 rounded-2xl" : ""}>
              <PlanCard plan={p} onSelect={() => activate(p)} busy={busy === p.id} current={current === p.id} />
            </div>
          ))}
          <PlanCard plan={CUSTOM_PLAN} featured />
        </div>
        <div className="nadia-card p-6 mt-6" data-testid="plans-packs">
          <h2 className="font-heading text-lg font-semibold">Ricarica i gettoni</h2>
          <p className="text-sm text-slate-400 mt-1 mb-4">Con Start o Pro attivo: 10 € = 100 gettoni Nadia. Ricarica minima 10 €. Importi IVA esclusa.</p>
          <div className="grid grid-cols-3 gap-3">
            {(cfg?.packs || []).map((p) => (
              <button key={p.id} onClick={() => buyFlex(p)} disabled={!!busy} className="rounded-xl border border-white/10 p-3 hover:border-cyan-400/50" data-testid={`pack-${p.credits}`}>
                <div className="font-bold text-lg">{p.credits} gettoni</div><div className="text-amber-300">{eur(p.price)}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Anche la chat usa gettoni: il consumo varia con l’elaborazione. La quota prenotata e non utilizzata viene restituita.</p>
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck size={13} className="text-emerald-400" /> {user ? "" : "Prima crei l'account (nome, email, password), poi il pagamento. "}Pagamento {cfg?.checkout?.connected ? "tramite Stripe" : "non ancora disponibile"} · prezzi IVA esclusa · disdici quando vuoi.</div>

        {/* Sempre incluso */}
        <div className="nadia-card p-6 mt-8" data-testid="plans-free">
          <div className="flex items-center gap-2 mb-2"><Coins size={15} className="text-emerald-300" /><h2 className="font-heading text-base md:text-lg font-semibold text-slate-100">Consultazione inclusa · zero gettoni</h2></div>
          <p className="text-sm text-slate-300 mb-3">{cfg?.free_message}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {(cfg?.free_features || []).map((f) => <div key={f} className="flex items-center gap-1.5 text-xs text-slate-300"><Check size={11} className="text-emerald-400 shrink-0" />{f}</div>)}
          </div>
        </div>

        {/* Costi */}
        <div className="mt-5">
          <div className="nadia-card p-6" data-testid="plans-costs">
            <h2 className="font-heading text-base md:text-lg font-semibold text-slate-100 mb-3">Consumi AI</h2>
            <div className="space-y-1.5">
              {Object.entries(cfg?.costs || {}).map(([id, c]) => (
                <div key={id} className="flex items-center justify-between text-sm border-b border-white/5 pb-1.5" data-testid={`cost-${id}`}>
                  <span className="text-slate-300">{c.label}{c.note ? <span className="text-slate-500 text-xs"> · {c.note}</span> : null}</span>
                  <span className={`font-semibold ${c.credits ? "text-amber-300" : "text-emerald-300"}`}>{c.pricing_pending ? "Tariffa in verifica" : c.variable ? "A consumo" : c.credits ? `${c.credits} gettoni` : "incluso"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>


        <div className="mt-8 text-center text-xs text-slate-500"><Link to="/" className="hover:text-slate-300">← Torna alla home</Link>{current && <> · <Link to="/app" className="text-cyan-300 hover:text-cyan-200" data-testid="plans-go-app">Vai alla dashboard</Link></>}</div>
      </div>
    </div>
  );
}
